import { useEffect, useState, useMemo, type ReactNode } from 'react'
import {
  ActionIcon,
  Combobox,
  Paper,
  Pill,
  PillsInput,
  ScrollArea,
  Text,
  useCombobox,
} from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import { useMedplum } from '@medplum/react'
import { useDebounce } from '@/hooks'
import type { ValueSetExpansionContains } from '@medplum/fhirtypes'
import { logError } from './errorUtils'
import { useSeedDiagnosisCodes } from './useSeedDiagnosisCodes'
import type { DiagnosisDefault } from './types'

const ICD10CM_VALUE_SET_URL = 'https://behuman.health/fhir/ValueSet/icd10cm-2026'

const EMPTY_DEFAULTS: DiagnosisDefault[] = []

type ExpansionNode = ValueSetExpansionContains & { contains?: ValueSetExpansionContains[] }

const flattenContains = (nodes: ValueSetExpansionContains[] = []): ValueSetExpansionContains[] => {
  const flat: ValueSetExpansionContains[] = []
  const queue = [...nodes] as ExpansionNode[]
  while (queue.length > 0) {
    const current = queue.shift()
    if (!current) continue
    flat.push(current)
    if (Array.isArray(current.contains) && current.contains.length > 0) {
      queue.push(...(current.contains as ExpansionNode[]))
    }
  }
  return flat
}

const extractCode = (line: string): string => line.split(/\s/)[0]?.toUpperCase() || ''

type Props = {
  value: string
  onChange: (value: string) => void
  error?: ReactNode
  /** Active-condition ICD-10 codes used to pre-fill the field when it is empty. */
  defaultCodes?: DiagnosisDefault[]
  /**
   * The parent form pre-filled the field itself — for forms whose steps unmount this
   * section, where seeding here would re-add removed codes on every return (PD-1822).
   */
  autoFilled?: boolean
}

export const DiagnosisCodesSection = ({
  value,
  onChange,
  error,
  defaultCodes = EMPTY_DEFAULTS,
  autoFilled: autoFilledByParent = false,
}: Props) => {
  const medplum = useMedplum()
  const combobox = useCombobox({ onDropdownClose: () => combobox.resetSelectedOption() })

  const seededHere = useSeedDiagnosisCodes({
    currentValue: value,
    defaults: defaultCodes,
    onSeed: onChange,
  })
  const autoFilled = seededHere || autoFilledByParent

  const [searchValue, setSearchValue] = useState('')
  const [searchResults, setSearchResults] = useState<{ code: string; display: string }[]>([])
  const [loading, setLoading] = useState(false)
  const [searchError, setSearchError] = useState(false)

  const debouncedSearch = useDebounce(searchValue, 250)

  const parsedCodes = useMemo(() => value.split('\n').filter(Boolean), [value])

  const existingCodes = useMemo(() => new Set(parsedCodes.map(extractCode)), [parsedCodes])

  // Search ICD-10 codes via ValueSet expansion
  useEffect(() => {
    let active = true
    const filter = debouncedSearch.trim()

    if (filter.length < 2) {
      setSearchResults([])
      setLoading(false)
      return
    }

    setLoading(true)
    medplum
      .valueSetExpand({
        url: ICD10CM_VALUE_SET_URL,
        filter,
        count: 20,
        offset: 0,
      })
      .then((expanded) => {
        if (!active) return
        const results = flattenContains(expanded.expansion?.contains || [])
          .filter((item) => Boolean(item.code))
          .map((item) => ({
            code: item.code!,
            display: item.display || item.code!,
          }))
          .filter((r) => !existingCodes.has(r.code.toUpperCase()))
        setSearchResults(results)
        setSearchError(false)
      })
      .catch((err) => {
        logError('ICD-10 search', err)
        if (active) {
          setSearchResults([])
          setSearchError(true)
        }
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [debouncedSearch, medplum, existingCodes])

  const handleAdd = (codeString: string) => {
    const updated = [...parsedCodes, codeString]
    onChange(updated.join('\n'))
    setSearchValue('')
    combobox.closeDropdown()
  }

  const handleRemove = (line: string) => {
    const updated = parsedCodes.filter((c) => c !== line)
    onChange(updated.join('\n'))
  }

  return (
    <Paper p="md" withBorder>
      <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
        ICD-10 / DIAGNOSIS CODES
      </Text>
      <Text size="xs" c="dimmed" mb="sm">
        Search and add the appropriate ICD-10 diagnosis codes for this order.
      </Text>
      {autoFilled && parsedCodes.length > 0 && (
        <Text size="xs" c="#316dab" mb="sm">
          Pre-filled from active diagnoses — review before submitting.
        </Text>
      )}

      <Combobox store={combobox} onOptionSubmit={handleAdd}>
        <Combobox.Target>
          <PillsInput
            error={error}
            onClick={() => combobox.openDropdown()}
            rightSection={
              searchValue ? (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  aria-label="Clear search"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSearchValue('')
                    combobox.closeDropdown()
                  }}
                >
                  <IconX size={14} />
                </ActionIcon>
              ) : null
            }
            rightSectionPointerEvents={searchValue ? 'all' : 'none'}
          >
            <Pill.Group>
              {parsedCodes.map((line) => (
                <Pill key={line} withRemoveButton onRemove={() => handleRemove(line)}>
                  {line}
                </Pill>
              ))}
              <Combobox.EventsTarget>
                <PillsInput.Field
                  placeholder={parsedCodes.length === 0 ? 'Search ICD-10 codes...' : 'Add more...'}
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.currentTarget.value)
                    combobox.openDropdown()
                  }}
                  onFocus={() => combobox.openDropdown()}
                  onBlur={() => combobox.closeDropdown()}
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' && searchValue === '' && parsedCodes.length > 0) {
                      handleRemove(parsedCodes[parsedCodes.length - 1])
                    }
                  }}
                />
              </Combobox.EventsTarget>
            </Pill.Group>
          </PillsInput>
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>
            <ScrollArea.Autosize mah={280} type="scroll">
              {loading && <Combobox.Empty>Searching...</Combobox.Empty>}
              {!loading && searchError && (
                <Combobox.Empty>
                  <Text c="red" size="sm">
                    Search failed — please try again
                  </Text>
                </Combobox.Empty>
              )}
              {!loading &&
                !searchError &&
                searchResults.length === 0 &&
                debouncedSearch.trim().length >= 2 && (
                  <Combobox.Empty>No results found</Combobox.Empty>
                )}
              {!loading &&
                searchResults.length === 0 &&
                debouncedSearch.trim().length < 2 &&
                debouncedSearch.trim().length > 0 && (
                  <Combobox.Empty>Type at least 2 characters to search</Combobox.Empty>
                )}
              {searchResults.map((r) => (
                <Combobox.Option value={`${r.code} ${r.display}`} key={r.code}>
                  <Text component="span" fw={600} size="sm">
                    {r.code}
                  </Text>{' '}
                  <Text component="span" size="sm" c="dimmed">
                    {r.display}
                  </Text>
                </Combobox.Option>
              ))}
            </ScrollArea.Autosize>
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </Paper>
  )
}
